// Resolve the trace id + backend to stamp on a bug report.
//
// A bug report must link to the forecast the user is *looking at* when they
// hit report. Historically the report read `lastObs` — a transient
// observability value written only when a fresh forecast completes in the
// current mount (App.jsx doForecast) or on resume reattach. That value is
// null after navigating between saved forecasts, after a superseded
// background completion, or after a reload that restores an already-completed
// row — so the report silently dropped the trace even though the forecast was
// right there on screen (bug cf6e0768: trace present in server logs AND on the
// active forecast, but `forecaster_trace_id` null on the report row).
//
// The active forecast object reliably carries `traceId` — stamped onto it at
// `traceAllocated` time by `onTraceAllocated`, before any completion — and
// `_obs` once it finishes. So anchor on the active forecast first; the
// in-flight `activeTrace` covers the brief window before the stamp lands; and
// `lastObs` stays as a final fallback for legacy / non-streaming forecasts.
//
// Neither fallback may describe a different forecast. `lastObs` is whatever completed
// last, in any forecast, so it is used only when NO forecast is on screen. `activeTrace`
// is this tab's in-flight stream, which can belong to a sibling row, so it is used only
// when no forecast is on screen, when it matches the one that is, or when that one has
// no identity yet. A trace-less forecast on screen (a tour fixture, a copied row)
// reports null.
export function resolveBugReportTrace({ active, activeTrace, lastObs } = {}) {
  // Each source describes one forecast as a (traceId, backend) pair. `backend` is
  // the run's engine/orchestrator (mirrors run.backend; NOT the x-forecast-backend
  // deploy label), which differs per forecast — compare legs run different engines —
  // so a traceId from one forecast must never be paired with a backend borrowed
  // from another, or the report mislabels which engine served the forecast under
  // view. So: resolve traceId by precedence, then take backend only from a source
  // whose traceId matches it.
  //
  // `active` carries `traceId` top-level (stamped at traceAllocated) but no
  // top-level backend — backend only lands on `active._obs` at completion. During
  // the in-flight window the matching backend lives on the `activeTrace` handle
  // (same forecast, same traceId), so backend-matching must look across sources,
  // not just the one that supplied the traceId.
  const lastObsSource = lastObs && {
    traceId: lastObs.traceId || null,
    backend: lastObs.backend || null,
  };
  const activeTraceId = active
    ? active.traceId || (active._obs && active._obs.traceId) || null
    : null;
  const inFlightIsActive =
    !!activeTrace &&
    (!active ||
      (!activeTraceId && !active.forecastId) ||
      (!!activeTrace.traceId && activeTrace.traceId === activeTraceId) ||
      (!!activeTrace.forecastId && activeTrace.forecastId === active.forecastId));
  const sources = [
    active && { traceId: activeTraceId, backend: (active._obs && active._obs.backend) || null },
    inFlightIsActive && {
      traceId: activeTrace.traceId || null,
      backend: activeTrace.backend || null,
    },
    !active && lastObsSource,
  ].filter((s) => s && s.traceId);

  const traceId = sources.length ? sources[0].traceId : null;
  let backend = null;
  if (traceId) {
    const match = [...sources, lastObsSource].find((s) => s && s.traceId === traceId && s.backend);
    backend = match ? match.backend : null;
  }
  return { traceId, backend };
}
